import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const RetailLanding = () => {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: '/retail',
      audience: 'Retail'
    });
  }, []);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>URGAA for Retail Chains - Drive Footfall with EV Charging | Go4Garage</title>
        <meta name="title" content="URGAA for Retail Chains - Drive Footfall with EV Charging" />
        <meta name="description" content="Turn parking lots into revenue generators. EV charging increases dwell time by 30 minutes, basket size by 25%. Perfect for malls, supermarkets, QSRs, petrol pumps. 100+ retail chains use URGAA." />
        <meta name="keywords" content="retail EV charging, mall charging stations, supermarket EV parking, QSR charging, petrol pump EV charging, retail footfall increase India" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urgaa.in/retail" />
        <meta property="og:title" content="URGAA for Retail Chains - Drive Footfall with EV Charging" />
        <meta property="og:description" content="Turn parking lots into revenue generators. Increase dwell time and basket size with EV charging." />
        <meta property="og:image" content="https://urgaa.in/images/og-retail.jpg" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://urgaa.in/retail" />
        <meta name="twitter:title" content="URGAA for Retail Chains" />
        <meta name="twitter:description" content="EV charging infrastructure for retail locations" />
        <meta name="twitter:image" content="https://urgaa.in/images/twitter-retail.jpg" />
        <meta name="twitter:site" content="@Go4Garage" />
        
        {/* Additional Meta */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urgaa.in/retail" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "URGAA for Retail Chains",
            "serviceType": "EV Charging Infrastructure for Retail",
            "provider": {
              "@type": "Organization",
              "name": "Go4Garage Private Limited",
              "url": "https://urgaa.in"
            },
            "areaServed": "IN",
            "description": "EV charging solutions for retail chains to drive footfall and revenue"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-pink-100 text-pink-800 rounded-full text-sm font-semibold mb-6">
              🛒 100+ Retail Chains | 30% Higher Dwell Time
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Charging Stations That<br />Drive Footfall & Sales
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              EV drivers spend 30 minutes longer at your location. 25% higher basket size. Turn your parking lot into a revenue generator.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/login" className="px-8 py-4 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-semibold">
                Get Location Analysis
              </Link>
              <a href="#impact" className="px-8 py-4 border-2 border-pink-600 text-pink-600 rounded-lg hover:bg-pink-50 font-semibold">
                See Impact
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-4">Zero capex | 45-day rollout | Works for all retail formats</p>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Why Retail Chains Need EV Charging</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-blue-600 text-4xl mb-4">⏱️</div>
                <h3 className="text-xl font-bold mb-2">+30 Minutes Dwell Time</h3>
                <p className="text-gray-600">EV drivers stay longer while charging. More browsing = more purchases.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-green-600 text-4xl mb-4">🛒</div>
                <h3 className="text-xl font-bold mb-2">+25% Basket Size</h3>
                <p className="text-gray-600">EV owners are premium demographic. Higher income = higher spending.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-purple-600 text-4xl mb-4">📍</div>
                <h3 className="text-xl font-bold mb-2">Destination Preference</h3>
                <p className="text-gray-600">70% of EV drivers choose locations based on charging availability.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section id="impact" className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Perfect for All Retail Formats</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-2 border-pink-500 p-6 rounded-lg">
                <div className="text-pink-600 text-4xl mb-4">🏬</div>
                <h3 className="text-xl font-bold mb-2">Malls & Shopping Centers</h3>
                <p className="text-gray-600 mb-4">Fast chargers (30kW-60kW) keep shoppers engaged for 45-60 minutes</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Premium parking with charging</li>
                  <li>✓ Loyalty program integration</li>
                  <li>✓ Promotional offers while charging</li>
                  <li>✓ Typical: 8-12 chargers per location</li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 p-6 rounded-lg">
                <div className="text-blue-600 text-4xl mb-4">🛒</div>
                <h3 className="text-xl font-bold mb-2">Supermarkets & Hypermarkets</h3>
                <p className="text-gray-600 mb-4">AC chargers (7kW) for 30-45 minute shopping trips</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Free/subsidized charging for loyalty members</li>
                  <li>✓ Push notifications about in-store offers</li>
                  <li>✓ Increased weekly visit frequency</li>
                  <li>✓ Typical: 4-8 chargers per location</li>
                </ul>
              </div>
              <div className="border-2 border-green-500 p-6 rounded-lg">
                <div className="text-green-600 text-4xl mb-4">🍔</div>
                <h3 className="text-xl font-bold mb-2">QSRs & Food Courts</h3>
                <p className="text-gray-600 mb-4">Quick DC chargers (15-20 minutes) for meal breaks</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Combo meals with charging vouchers</li>
                  <li>✓ Drive repeat visits (charging + dining)</li>
                  <li>✓ Mobile app integration for pre-ordering</li>
                  <li>✓ Typical: 2-4 chargers per location</li>
                </ul>
              </div>
              <div className="border-2 border-orange-500 p-6 rounded-lg">
                <div className="text-orange-600 text-4xl mb-4">⛽</div>
                <h3 className="text-xl font-bold mb-2">Petrol Pumps & Fuel Stations</h3>
                <p className="text-gray-600 mb-4">Future-proof your business with EV charging alongside fuel</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Diversify revenue streams</li>
                  <li>✓ Higher margins than fuel (₹12 vs ₹3/liter)</li>
                  <li>✓ Cross-sell convenience store items</li>
                  <li>✓ Typical: 4-6 fast chargers per pump</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Features */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">URGAA Retail Solution</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">📋 Branded Experience</h3>
                <p className="text-sm text-gray-600">Co-brand charging stations with your logo. In-app promotions to charging customers.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">📊 Analytics Dashboard</h3>
                <p className="text-sm text-gray-600">Track footfall increase, dwell time, and incremental sales from EV customers.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">🔗 Loyalty Integration</h3>
                <p className="text-sm text-gray-600">Link charging to your existing loyalty program. Earn points while charging.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">📱 Push Marketing</h3>
                <p className="text-sm text-gray-600">Send offers and promotions to customers while they're charging at your location.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">⚡ Multi-Location Rollout</h3>
                <p className="text-sm text-gray-600">Standardized installation across 50-100+ locations in 90 days.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-bold mb-3">🔧 Zero Operations Hassle</h3>
                <p className="text-sm text-gray-600">We handle all maintenance, customer support, payment collections.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-8">Flexible Pricing Models</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-pink-600 mb-3">Revenue Share</h3>
                <div className="text-4xl font-bold mb-2">35-40%</div>
                <div className="text-sm text-gray-600 mb-4">of charging revenue to you</div>
                <ul className="text-left text-sm text-gray-600 space-y-2 mb-4">
                  <li>✓ Zero capex from you</li>
                  <li>✓ We install, own, operate chargers</li>
                  <li>✓ You get monthly revenue share</li>
                  <li>✓ Best for: Malls, supermarkets</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-blue-600 mb-3">Subsidized/Free</h3>
                <div className="text-4xl font-bold mb-2">Loss Leader</div>
                <div className="text-sm text-gray-600 mb-4">attract premium customers</div>
                <ul className="text-left text-sm text-gray-600 space-y-2 mb-4">
                  <li>✓ Offer free/subsidized charging</li>
                  <li>✓ Increase footfall & basket size</li>
                  <li>✓ Fixed monthly fee (₹3K-5K/charger)</li>
                  <li>✓ Best for: Premium malls, luxury retail</li>
                </ul>
              </div>
            </div>
            <div className="mt-8">
              <Link to="/login" className="px-8 py-4 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-semibold inline-block">
                Get Custom Proposal
              </Link>
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="py-16 bg-gradient-to-br from-pink-50 to-purple-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-8">Success Story: Leading Supermarket Chain</h2>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-pink-600 mb-1">+22%</div>
                  <div className="text-sm text-gray-600">Footfall increase on weekends</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600 mb-1">₹28L</div>
                  <div className="text-sm text-gray-600">Additional annual revenue (12 locations)</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600 mb-1">35 min</div>
                  <div className="text-sm text-gray-600">Average dwell time increase</div>
                </div>
              </div>
              <p className="text-sm text-gray-600 italic">
                "We installed 4 AC chargers at each of our 12 Bangalore locations. Within 3 months, we saw a 22% increase in weekend footfall and EV customers spent 28% more on average. The ROI was positive from month 1." - Operations Head, Premium Supermarket Chain
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-pink-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Drive Footfall?</h2>
            <p className="text-xl mb-8">100+ retail chains already boosting sales with URGAA</p>
            <Link to="/login" className="px-8 py-4 bg-white text-pink-600 rounded-lg hover:bg-gray-100 font-semibold inline-block">
              Get Location Analysis →
            </Link>
            <p className="text-sm mt-4 opacity-90">Zero capex | 45-day rollout | Multi-location support</p>
          </div>
        </section>
      </div>
    </>
  );
};

export default RetailLanding;